import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Pencil, Check, Download, ArrowLeft, Send, Sparkles, Mic } from "lucide-react";
import api, { getToken } from "../api";
import { Spinner, Pill, Btn, StatusPill, VisibilityBadge } from "../ui";
import VoiceMode from "../VoiceMode";

const TABS = ["overview", "interview", "brief", "analysis", "board", "paths", "decision", "plan", "review", "resolved"];
const TAB_LABELS = { overview: "Overview", interview: "Interview", brief: "Brief", analysis: "Analysis", board: "Board", paths: "Paths", decision: "Decision", plan: "Plan", review: "Review", resolved: "Resolved" };

function KV({ obj }) {
  if (!obj) return null;
  if (typeof obj === "string") return <p className="text-sm leading-relaxed text-stone-300">{obj}</p>;
  return (
    <div className="space-y-4">
      {Object.entries(obj).map(([k, v]) => {
        if (v == null || v === "") return null;
        const label = k.replace(/_/g, " ");
        if (Array.isArray(v)) {
          return (
            <div key={k}>
              <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-2">{label}</p>
              <ul className="space-y-1.5">
                {v.map((item, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-stone-300">
                    <span className="mt-[9px] w-1 h-1 rounded-full bg-[#B5A58A]/70 shrink-0" />
                    {typeof item === "string" ? item : item.content || item.title || item.action || JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        if (typeof v === "object") return null;
        return (
          <div key={k}>
            <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">{label}</p>
            <p className="text-sm leading-relaxed text-stone-300">{String(v)}</p>
          </div>
        );
      })}
    </div>
  );
}

function GeneratePanel({ label, description, onGenerate, busy, testid }) {
  return (
    <div className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-8 text-center">
      <Sparkles className="w-6 h-6 text-[#B5A58A] mx-auto mb-4" strokeWidth={1.5} />
      <p className="text-sm text-stone-400 leading-relaxed max-w-md mx-auto mb-6">{description}</p>
      <Btn testid={testid} onClick={onGenerate} disabled={busy}>{busy ? "Consulting the board…" : label}</Btn>
    </div>
  );
}

export default function Workspace() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [kase, setKase] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const threadRef = useRef(null);

  const load = () => api.get(`/cases/${id}`).then((r) => setKase(r.data)).catch(() => toast.error("Case not found."));
  useEffect(() => { setKase(null); load(); }, [id]);
  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight }); }, [kase?.messages?.length, sending]);

  const setTab = (t) => setParams({ tab: t });

  const saveTitle = async () => {
    setEditingTitle(false);
    if (!titleDraft.trim() || titleDraft === kase.title) return;
    const r = await api.patch(`/cases/${id}`, { title: titleDraft.trim() });
    setKase((k) => ({ ...k, title: r.data.title || titleDraft.trim() }));
  };

  const sendTurn = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setDraft("");
    try {
      const r = await api.post(`/cases/${id}/interview`, { user_message: content });
      await load();
      if (r.data.ready_for_analysis) toast.success("The brief is ready — run the analysis.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "The advisor is unavailable — try again.");
    } finally {
      setSending(false);
    }
  };

  const generate = async (endpoint, nextTab) => {
    setBusy(true);
    try {
      await api.post(`/cases/${id}/${endpoint}`);
      await load();
      if (nextTab) setTab(nextTab);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!kase) return <Spinner label="Opening the case file…" />;

  const openPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${id}/pdf?token=${getToken()}`;
    window.open(url, "_blank");
  };

  const messages = kase.messages || [];
  const grouped = (kase.brief?.findings || []).reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f.content);
    return acc;
  }, {});

  return (
    <div data-testid="workspace-page">
      <Link to="/app/cases" data-testid="workspace-back" className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-[0.2em] uppercase text-stone-400 hover:text-white transition-colors duration-300 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Cases
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <p className="font-mono-label text-[10px] tracking-[0.25em] text-[#B5A58A]/80">{kase.public_case_number}</p>
            <StatusPill status={kase.status} />
            <VisibilityBadge kase={kase} isGuest={kase.is_guest} onChange={(updated) => setKase((k) => ({ ...k, visibility: updated.visibility }))} />
          </div>
          {editingTitle ? (
            <div className="flex items-center gap-3">
              <input
                data-testid="title-edit-input"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveTitle()}
                autoFocus
                className="bg-[#26241F] border border-[#B5A58A]/50 rounded-xl px-4 py-2 font-serif-display text-2xl text-white outline-none"
              />
              <button data-testid="title-save-btn" onClick={saveTitle} className="text-[#B5A58A]"><Check className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
          ) : (
            <h1 className="font-serif-display font-light tracking-tight text-4xl md:text-5xl text-white flex items-center gap-3 flex-wrap">
              {kase.title}
              <button data-testid="title-edit-btn" onClick={() => { setTitleDraft(kase.title); setEditingTitle(true); }} className="text-stone-500 hover:text-[#B5A58A] transition-colors duration-300">
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </h1>
          )}
          <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mt-3">
            Primary advisor · {kase.advisor?.name || "Auto — pending"}
          </p>
        </div>
        <Btn testid="pdf-btn" variant="outline" onClick={openPdf}>
          <Download className="w-3.5 h-3.5 inline mr-2" strokeWidth={1.5} /> PDF
        </Btn>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-white/[0.08] mb-8" data-testid="workspace-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`font-mono-label text-[9px] tracking-[0.2em] uppercase px-4 py-3 whitespace-nowrap transition-colors duration-300 ${
              tab === t ? "text-[#B5A58A] border-b-2 border-[#B5A58A]" : "text-stone-500 hover:text-white"
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {tab === "overview" && (
          <div className="space-y-8 max-w-3xl" data-testid="tab-content-overview">
            <div>
              <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-2">Situation</p>
              <p className="text-sm md:text-base leading-relaxed text-stone-300">{kase.situation}</p>
            </div>
            {kase.desired_outcome && (
              <div>
                <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-2">Desired outcome</p>
                <p className="text-sm leading-relaxed text-stone-300">{kase.desired_outcome}</p>
              </div>
            )}
            {(kase.timeline || []).length > 0 && (
              <div>
                <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-3">Timeline</p>
                <div className="space-y-2">
                  {kase.timeline.map((t, i) => (
                    <div key={i} className="flex gap-3 text-xs text-stone-400">
                      <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#B5A58A]/60 shrink-0" />
                      <span>{t.event}{t.note ? ` — ${t.note}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "interview" && (
          <div className="max-w-3xl" data-testid="tab-content-interview">
            <div className="flex items-center justify-between gap-4 mb-5">
              <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500">
                Question {kase.question_count} of 5 — cap engaged when the brief has enough coverage
              </p>
              {!kase.ready_for_analysis && (
                <button
                  data-testid="voice-mode-btn"
                  onClick={() => setVoiceOpen(true)}
                  className="shrink-0 flex items-center gap-2 border border-[#B5A58A]/40 rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#B5A58A] hover:border-[#B5A58A] transition-colors duration-300"
                >
                  <Mic className="w-3.5 h-3.5" strokeWidth={1.5} /> Voice Mode
                </button>
              )}
            </div>
            <div ref={threadRef} className="space-y-4 max-h-[46vh] overflow-y-auto pr-1 mb-6">
              {messages.map((m, i) => (
                <div key={i} className={`rounded-2xl px-5 py-4 text-sm leading-relaxed ${m.role === "user" ? "bg-[#B5A58A]/10 text-stone-200 ml-10" : "bg-[#26241F] border border-white/[0.08] text-stone-300 mr-10"}`}>
                  <p className="font-mono-label text-[8px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">{m.role === "user" ? "You" : kase.advisor?.name || "Advisor"}</p>
                  {m.content}
                </div>
              ))}
              {sending && (
                <div className="bg-[#26241F] border border-white/[0.08] rounded-2xl px-5 py-4 mr-10">
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-[#B5A58A]/30 border-t-[#B5A58A] animate-spin" />
                </div>
              )}
            </div>
            {kase.ready_for_analysis ? (
              <GeneratePanel
                label="Run the analysis"
                description="The advisor has enough. Generate the full analysis, then hear from the advisory board."
                onGenerate={() => generate("analysis", "analysis")}
                busy={busy}
                testid="run-analysis-btn"
              />
            ) : (
              <div className="flex items-end gap-3">
                <textarea
                  data-testid="interview-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTurn(); } }}
                  rows={2}
                  placeholder="Answer plainly…"
                  className="flex-1 bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300 resize-none"
                />
                <button
                  data-testid="interview-send-btn"
                  onClick={sendTurn}
                  disabled={!draft.trim() || sending}
                  className="bg-[#B5A58A] text-[#1F1E1B] rounded-full p-3.5 hover:bg-[#c4b59a] disabled:opacity-40 transition-colors duration-300"
                >
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "brief" && (
          <div className="max-w-3xl space-y-6" data-testid="tab-content-brief">
            {kase.brief?.situation && <KV obj={{ situation: kase.brief.situation, desired_outcome: kase.brief.desired_outcome, central_tension: kase.brief.central_tension, potential_decision: kase.brief.potential_decision }} />}
            {Object.keys(grouped).length > 0 ? (
              Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-[#B5A58A]/70 mb-2">{cat}</p>
                  <ul className="space-y-1.5">
                    {items.map((it, i) => (
                      <li key={i} className="flex gap-3 text-sm leading-relaxed text-stone-300">
                        <span className="mt-[9px] w-1 h-1 rounded-full bg-[#B5A58A]/70 shrink-0" />{it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <p className="text-sm text-stone-500">The brief writes itself as the interview proceeds.</p>
            )}
          </div>
        )}

        {tab === "analysis" && (
          <div className="max-w-3xl" data-testid="tab-content-analysis">
            {kase.analysis ? <KV obj={kase.analysis} /> : (
              <GeneratePanel
                label="Generate analysis"
                description={kase.ready_for_analysis ? "The brief is ready. Get the advisor's full read of the situation." : "Finish the interview first — the advisor needs 3 to 5 answers before the analysis."}
                onGenerate={() => generate("analysis")}
                busy={busy || !kase.ready_for_analysis}
                testid="generate-analysis-btn"
              />
            )}
            {kase.analysis && !kase.advisory_board?.length && (
              <div className="mt-8"><Btn testid="to-board-btn" variant="outline" onClick={() => generate("advisory-board", "board")} disabled={busy}>Convene the advisory board</Btn></div>
            )}
          </div>
        )}

        {tab === "board" && (
          <div className="max-w-3xl" data-testid="tab-content-board">
            {(kase.advisory_board || []).length > 0 ? (
              <div className="space-y-4">
                {kase.advisory_board.map((a, i) => (
                  <div key={i} className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-6">
                    <p className="font-serif-display text-2xl text-white mb-2">{a.role_name}</p>
                    <p className="text-sm leading-relaxed text-stone-400">{a.main_observation}</p>
                    {a.suggested_question && <p className="text-xs text-stone-500 mt-3 italic">"{a.suggested_question}"</p>}
                  </div>
                ))}
                {!kase.paths?.length && (
                  <Btn testid="to-paths-btn" variant="outline" onClick={() => generate("paths", "paths")} disabled={busy}>Map the three paths</Btn>
                )}
              </div>
            ) : (
              <GeneratePanel
                label="Convene the board"
                description={kase.analysis ? "Three outside perspectives weigh in on your analysis." : "Run the analysis first — the board needs it on the table."}
                onGenerate={() => generate("advisory-board")}
                busy={busy || !kase.analysis}
                testid="generate-board-btn"
              />
            )}
          </div>
        )}

        {tab === "paths" && (
          <div className="max-w-3xl" data-testid="tab-content-paths">
            {(kase.paths || []).length > 0 ? (
              <div className="space-y-4">
                {kase.paths.map((p, i) => (
                  <div key={i} className="bg-[#26241F] border border-white/[0.08] rounded-2xl p-6">
                    <Pill tone={p.path_type === "BOLD" ? "warning" : p.path_type === "CAUTIOUS" ? "neutral" : "accent"}>{p.path_type}</Pill>
                    <p className="font-serif-display text-2xl text-white mt-3 mb-2">{p.title}</p>
                    <p className="text-sm leading-relaxed text-stone-400 mb-3">{p.description}</p>
                    <KV obj={{ first_steps: p.first_steps, key_risk: p.key_risk, best_if: p.best_if }} />
                  </div>
                ))}
              </div>
            ) : (
              <GeneratePanel
                label="Map the paths"
                description={kase.analysis ? "Cautious, balanced, bold — three realistic routes, tradeoffs made explicit." : "Run the analysis first."}
                onGenerate={() => generate("paths")}
                busy={busy || !kase.analysis}
                testid="generate-paths-btn"
              />
            )}
          </div>
        )}

        {tab === "decision" && (
          <DecisionTab kase={kase} id={id} reload={load} busy={busy} setBusy={setBusy} />
        )}

        {tab === "plan" && (
          <PlanTab kase={kase} id={id} reload={load} busy={busy} setBusy={setBusy} generate={generate} />
        )}

        {tab === "review" && (
          <ReviewTab kase={kase} id={id} reload={load} busy={busy} setBusy={setBusy} />
        )}

        {tab === "resolved" && (
          <div className="max-w-3xl" data-testid="tab-content-resolved">
            {kase.status === "RESOLVED" ? (
              <div className="bg-[#26241F] border border-[#637A61]/40 rounded-2xl p-8">
                <Pill tone="success">Resolved</Pill>
                <p className="font-serif-display text-2xl text-white mt-4 mb-3">{kase.resolved_summary || "Case closed."}</p>
                <Btn testid="reopen-btn" variant="outline" onClick={() => generate("reopen", "overview")} disabled={busy}>Reopen this case</Btn>
              </div>
            ) : (
              <GeneratePanel
                label="Resolve the case"
                description={kase.review ? "Close the file. The outcome becomes part of your decision profile." : "Record a review first — what happened after you acted?"}
                onGenerate={() => generate("resolve")}
                busy={busy || !kase.review}
                testid="resolve-case-btn"
              />
            )}
          </div>
        )}
      </motion.div>

      <VoiceMode
        caseId={id}
        advisorName={kase.advisor?.name}
        open={voiceOpen}
        onClose={() => { setVoiceOpen(false); load(); }}
        onUpdate={load}
        onRunAnalysis={() => generate("analysis", "analysis")}
      />
    </div>
  );
}

function DecisionTab({ kase, id, reload, busy, setBusy }) {
  const [chosen, setChosen] = useState("");
  const [what, setWhat] = useState("");
  const [why, setWhy] = useState("");
  const [tradeoff, setTradeoff] = useState("");

  const submit = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/cases/${id}/decision`, {
        chosen_path_id: chosen || undefined,
        kind,
        what_decided: what || undefined,
        why: why || undefined,
        tradeoff_accepted: tradeoff || undefined,
      });
      await reload();
      toast.success("Decision recorded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record the decision.");
    } finally {
      setBusy(false);
    }
  };

  if (kase.decision) {
    return (
      <div className="max-w-3xl bg-[#26241F] border border-[#637A61]/40 rounded-2xl p-8" data-testid="tab-content-decision">
        <Pill tone="success">Decided</Pill>
        <div className="mt-4"><KV obj={kase.decision} /></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="tab-content-decision">
      {(kase.paths || []).length === 0 ? (
        <GeneratePanel label="Map the paths first" description="You need the three paths on the table before you decide." onGenerate={() => {}} busy testid="decision-locked" />
      ) : (
        <div className="space-y-5">
          <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-stone-500">Choose a path — or write your own call</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {kase.paths.map((p) => (
              <button
                key={p.path_type}
                data-testid={`decision-path-${p.path_type?.toLowerCase()}`}
                onClick={() => setChosen(chosen === p.path_type ? "" : p.path_type)}
                className={`text-left rounded-2xl border p-5 transition-colors duration-300 ${chosen === p.path_type ? "border-[#B5A58A] bg-[#B5A58A]/10" : "border-white/[0.1] bg-[#26241F] hover:border-white/25"}`}
              >
                <Pill tone={p.path_type === "BOLD" ? "warning" : p.path_type === "CAUTIOUS" ? "neutral" : "accent"}>{p.path_type}</Pill>
                <p className="font-serif-display text-lg text-white mt-2">{p.title}</p>
              </button>
            ))}
          </div>
          <textarea data-testid="decision-what" value={what} onChange={(e) => setWhat(e.target.value)} rows={2} placeholder="What did you decide?" className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 resize-none transition-colors duration-300" />
          <textarea data-testid="decision-why" value={why} onChange={(e) => setWhy(e.target.value)} rows={2} placeholder="Why? (optional)" className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 resize-none transition-colors duration-300" />
          <textarea data-testid="decision-tradeoff" value={tradeoff} onChange={(e) => setTradeoff(e.target.value)} rows={2} placeholder="Tradeoff you accept (optional)" className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 resize-none transition-colors duration-300" />
          <div className="flex gap-3 flex-wrap">
            <Btn testid="decision-submit-btn" disabled={busy || (!chosen && !what.trim())} onClick={() => submit(chosen ? "ACCEPTED" : "CUSTOM")}>Record the decision</Btn>
            <Btn testid="decision-not-ready-btn" variant="outline" disabled={busy} onClick={() => submit("NOT_READY")}>Not ready yet</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanTab({ kase, id, reload, busy, setBusy, generate }) {
  const toggle = async (aid, status) => {
    try {
      await api.patch(`/cases/${id}/actions/${aid}`, { status });
      await reload();
    } catch {
      toast.error("Could not update the action.");
    }
  };

  if (!kase.action_plan && !(kase.actions || []).length) {
    return (
      <div className="max-w-3xl" data-testid="tab-content-plan">
        <GeneratePanel
          label="Build the action plan"
          description={kase.decision ? "Turn the decision into dated, checkable actions." : "Record a decision first — the plan follows from it."}
          onGenerate={() => generate("action-plan")}
          busy={busy || !kase.decision}
          testid="generate-plan-btn"
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4" data-testid="tab-content-plan">
      {kase.action_plan && typeof kase.action_plan === "object" && <KV obj={kase.action_plan} />}
      {(kase.actions || []).map((a) => (
        <div key={a.id || a._id} className="flex items-center justify-between gap-4 bg-[#26241F] border border-white/[0.08] rounded-2xl px-6 py-4">
          <p className={`text-sm leading-relaxed ${a.status === "COMPLETED" ? "text-stone-500 line-through" : "text-stone-300"}`}>{a.title || a.content || a.action}</p>
          <button
            data-testid={`action-toggle-${a.id || a._id}`}
            onClick={() => toggle(a.id || a._id, a.status === "COMPLETED" ? "NOT_STARTED" : "COMPLETED")}
            className={`shrink-0 border rounded-full px-4 py-1.5 font-mono-label text-[9px] tracking-[0.15em] uppercase transition-colors duration-300 ${a.status === "COMPLETED" ? "border-[#637A61] text-[#8FA98D]" : "border-white/15 text-stone-400 hover:text-white"}`}
          >
            {a.status === "COMPLETED" ? "Done" : "Mark done"}
          </button>
        </div>
      ))}
    </div>
  );
}

function ReviewTab({ kase, id, reload, busy, setBusy }) {
  const [result, setResult] = useState("");
  const [lesson, setLesson] = useState("");
  const [outcome, setOutcome] = useState("expected");

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${id}/review`, {
        action_taken: true,
        result: result || "Reviewed",
        lesson,
        outcome_status: outcome,
      });
      await reload();
      toast.success("Review recorded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record the review.");
    } finally {
      setBusy(false);
    }
  };

  if (kase.review) {
    return (
      <div className="max-w-3xl bg-[#26241F] border border-white/[0.08] rounded-2xl p-8" data-testid="tab-content-review">
        <Pill tone="accent">Reviewed</Pill>
        <div className="mt-4"><KV obj={kase.review} /></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5" data-testid="tab-content-review">
      <p className="text-sm text-stone-400 leading-relaxed">What happened after you acted? This is where cases turn into judgment.</p>
      <textarea data-testid="review-result" value={result} onChange={(e) => setResult(e.target.value)} rows={3} placeholder="The result, honestly…" className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 resize-none transition-colors duration-300" />
      <textarea data-testid="review-lesson" value={lesson} onChange={(e) => setLesson(e.target.value)} rows={2} placeholder="The lesson for next time (optional)" className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl px-5 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 resize-none transition-colors duration-300" />
      <div className="flex gap-2 flex-wrap">
        {["better", "expected", "worse", "unfolding"].map((o) => (
          <button
            key={o}
            data-testid={`review-outcome-${o}`}
            onClick={() => setOutcome(o)}
            className={`rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase border transition-colors duration-300 ${outcome === o ? "border-[#B5A58A] text-[#B5A58A]" : "border-white/[0.12] text-stone-400 hover:text-white"}`}
          >
            {o}
          </button>
        ))}
      </div>
      <Btn testid="review-submit-btn" disabled={busy || !result.trim()} onClick={submit}>Record the review</Btn>
    </div>
  );
}
